import { useCallback } from "react";

import { AssignStatus, PriorityFilterStatus } from "@/enum/list.enum";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";

interface BulkStatusChangeArgs {
	id: number;
	selectedItems: { listPersonId: number }[];
	assignStatus?: AssignStatus;
	priorityFilterStatus: PriorityFilterStatus[];
}

interface UseInboxSingleProspectActionsArgs {
	/** The lead whose thread is open; nothing is sent when there is none. */
	listPerson: ListPerson | undefined;
	changeStatus: (args: BulkStatusChangeArgs) => Promise<unknown>;
	/** Closes the confirmation modal the action was launched from. */
	closeArchiveModal: () => void;
	closeDoneModal: () => void;
	notify: (message: string) => void;
	t: (key: string) => string;
}

/**
 * The Inbox's two per-lead status actions: archive the open lead, and mark it done.
 *
 * Both are the bulk status change applied to a single lead, followed by closing the
 * confirmation modal and telling the user what happened — extracted from `Inbox.tsx` so the
 * outcome (including the failure branch, which the page never rendered in a test) can be
 * exercised without mounting the page.
 */
export const useInboxSingleProspectActions = ({
	listPerson,
	changeStatus,
	closeArchiveModal,
	closeDoneModal,
	notify,
	t,
}: UseInboxSingleProspectActionsArgs) => {
	const applyStatus = useCallback(
		async (assignStatus: AssignStatus) =>
			changeStatus({
				id: listPerson?.list.id || 0,
				selectedItems: listPerson ? [{ listPersonId: listPerson.id }] : [],
				assignStatus,
				priorityFilterStatus: [PriorityFilterStatus.ALL],
			}),
		[changeStatus, listPerson]
	);

	const archiveProspect = useCallback(async () => {
		try {
			await applyStatus(AssignStatus.ARCHIVED);
			closeArchiveModal();
			notify(t("inbox.archiveSuccess"));
		} catch {
			notify(t("inbox.archiveError"));
		}
	}, [applyStatus, closeArchiveModal, notify, t]);

	const markProspectDone = useCallback(async () => {
		try {
			await applyStatus(AssignStatus.DONE);
			closeDoneModal();
			notify(t("inbox.markAsDoneSuccess"));
		} catch {
			notify(t("inbox.markAsDoneError"));
		}
	}, [applyStatus, closeDoneModal, notify, t]);

	return { archiveProspect, markProspectDone };
};
